import { ErrorMessage, Field, FormikProps } from "formik";
import Input from "../../atoms/Input";

export const ONE_MB = 1048576;
export const TEN_MB = 10485760;

export const validateFileSize =
  (maxSize: number) =>
  (file?: File | null): boolean =>
    file ? file.size <= maxSize : true;

export const StepSection = ({ children }: { children: React.ReactNode }) => (
  <div className="flex flex-col rounded-md bg-gray-100 px-[32px] py-[24px]">
    {children}
  </div>
);

export const FieldError = ({ name }: { name: string }) => (
  <ErrorMessage name={name}>
    {(msg) => <p className="mt-1 text-lg text-red-400">{msg}</p>}
  </ErrorMessage>
);

type FileFieldValues = {
  csvFile: File | null;
  drillImage: File | null;
  contentImagesZip: File | null;
  contentSoundsZip: File | null;
  contentPdfsZip: File | null;
};

const handleFileChange =
  (
    setFieldValue: FormikProps<FileFieldValues>["setFieldValue"],
    setFieldTouched: FormikProps<FileFieldValues>["setFieldTouched"],
    fieldName: keyof FileFieldValues,
  ) =>
  (e: React.SyntheticEvent<HTMLInputElement>) => {
    if (e.currentTarget?.files) {
      setFieldValue(fieldName, e.currentTarget.files[0]);
      setFieldTouched(fieldName, true, false);
    } else {
      setFieldValue(fieldName, null);
      setFieldTouched(fieldName, true, false);
    }
  };

type FileUploadFieldsProps = {
  setFieldValue: FormikProps<FileFieldValues>["setFieldValue"];
  setFieldTouched: FormikProps<FileFieldValues>["setFieldTouched"];
};

export const FileUploadFields = ({
  setFieldValue,
  setFieldTouched,
}: FileUploadFieldsProps) => (
  <div className="mt-[20px] flex flex-col gap-[10px]">
    <div className="flex flex-col gap-1">
      <label className="text-[12px] font-normal">
        CSVファイル<span className="text-red-400">*</span>
      </label>
      <Field
        component={Input}
        name="csvFile"
        type="file"
        accept=".csv,text/csv"
        onChange={handleFileChange(setFieldValue, setFieldTouched, "csvFile")}
      />
      <FieldError name="csvFile" />
    </div>
    <div className="flex flex-col gap-1">
      <label className="text-[12px] font-normal">
        表紙画像（jpg, png, gif）
      </label>
      <Field
        component={Input}
        name="drillImage"
        type="file"
        accept=".jpg,.jpeg,.png,.gif,image/jpeg,image/png,image/gif"
        onChange={handleFileChange(
          setFieldValue,
          setFieldTouched,
          "drillImage",
        )}
      />
      <FieldError name="drillImage" />
    </div>
    <div className="flex flex-col gap-1">
      <label className="text-[12px] font-normal">
        コンテンツ画像ZIP（jpg, png, gif、最大アップロードファイルサイズ：1MB）
      </label>
      <Field
        component={Input}
        name="contentImagesZip"
        type="file"
        accept=".zip,application/zip"
        onChange={handleFileChange(
          setFieldValue,
          setFieldTouched,
          "contentImagesZip",
        )}
      />
      <FieldError name="contentImagesZip" />
    </div>
    <div className="flex flex-col gap-1">
      <label className="text-[12px] font-normal">
        コンテンツ音声ZIP（mp3, wav,
        wave、最大アップロードファイルサイズ：10MB）
      </label>
      <Field
        component={Input}
        name="contentSoundsZip"
        type="file"
        accept=".zip,application/zip"
        onChange={handleFileChange(
          setFieldValue,
          setFieldTouched,
          "contentSoundsZip",
        )}
      />
      <FieldError name="contentSoundsZip" />
    </div>
    <div className="flex flex-col gap-1">
      <label className="text-[12px] font-normal">
        コンテンツPDFZIP（pdf、最大アップロードファイルサイズ：10MB）
      </label>
      <Field
        component={Input}
        name="contentPdfsZip"
        type="file"
        accept=".zip,application/zip"
        onChange={handleFileChange(
          setFieldValue,
          setFieldTouched,
          "contentPdfsZip",
        )}
      />
      <FieldError name="contentPdfsZip" />
    </div>
  </div>
);
